import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { ApiHook } from "../../hooks/apiHook";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { PhoneInput } from "react-international-phone";
import Select from "react-select";

function AddressModal({ show, setShow, forUser }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    control,
    reset,
    setError,
    setValue,
    trigger,
    formState: { errors },
  } = useForm();
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [districts, setDistricts] = useState([]);

  const addAddressMutation = ApiHook.CallAddAddress();
  const countries = ApiHook.CallGetCountries();
  const countryStates = ApiHook.CallGetStates();
  const stateDistricts = ApiHook.CallGetDistricts();

  const onSubmit = (data) => {
    if (data.phoneNumber.length <= 6) {
      setError("phoneNumber", {
        type: "manual",
        message: t("min_length"),
      });
      return;
    }
    data.forUserId = forUser?.address === 2 ? forUser.id : "";
    addAddressMutation.mutate(data, {
      onSuccess: (res) => {
        if (res?.status) {
          setShow(false);
          setSelectedCountry("");
          setSelectedState("");
          setSelectedDistrict("");
          reset();
          queryClient.invalidateQueries({ queryKey: ["get-address"] });
          toast.success(res?.message);
        } else {
          toast.error(res?.data?.description);
        }
      },
    });
  };

  const handleClose = () => {
    setShow(false);
    reset();
  };

  const changeCountry = (country) => {
    setSelectedCountry(country?.value);
    setSelectedState(null); // Clear the selected state
    setValue("state", null);
    setValue("country", country?.value); // Update the form value for country
  };

  const changeState = (state) => {
    setSelectedState(state?.value);
    setValue("state", state?.value);
  };
  const changeDistrict = (state) => {
    setSelectedDistrict(state?.value);
    setValue("district", state?.value);
  };

    useEffect(()=>{
      if(countries) {
        const defCountry = countries?.data?.find((country) => country)?.value;
        setSelectedCountry(defCountry);
        setValue("country", defCountry);
      }
    },[countries])

  useEffect(() => {
    if (selectedState && stateDistricts) {
      const filteredDistricts = stateDistricts?.data?.filter((d) => d.stateId === selectedState);
      setDistricts(filteredDistricts);
    }
  }, [selectedState]);

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t("add_address")}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>{t("name")}:</Form.Label>
              <Controller
                name="name"
                control={control}
                defaultValue=""
                rules={{
                  required: t("this_field_is_required"),
                  pattern: {
                    value: /^[a-zA-Z0-9\s]+$/,
                    message: t("invalid_format_space"),
                  },
                }}
                render={({ field }) => (
                  <>
                    <Form.Control {...field} type="text" />
                    {errors.name && (
                      <span className="text-danger">{errors.name.message}</span>
                    )}
                  </>
                )}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>{t("address")}:</Form.Label>
              <Controller
                name="address"
                control={control}
                defaultValue=""
                rules={{
                  required: t("this_field_is_required"),
                }}
                render={({ field }) => (
                  <>
                    <Form.Control as="textarea" {...field} type="text" />
                    {errors.address && (
                      <span className="text-danger">
                        {errors.address.message}
                      </span>
                    )}
                  </>
                )}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>{t("zipCode")}</Form.Label>
              <Controller
                name="zipCode"
                control={control}
                defaultValue=""
                rules={{
                  required: t("this_field_is_required"),
                  pattern: {
                    value: /^[0-9]+$/,
                    message: t("invalid_format_number"),
                  },
                }}
                render={({ field }) => (
                  <>
                    <Form.Control {...field} type="text" />
                    {errors.zipCode && (
                      <span className="text-danger">
                        {errors.zipCode.message}
                      </span>
                    )}
                  </>
                )}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Country</Form.Label>
              <Select
                // isSearchable={false}
                id="country"
                name="country"
                // className={` ${errors[item.code] ? "error-field" : ""}`}
                {...register("country", {
                  required: {
                    value: true,
                    message: t("this_field_is_required"),
                  },
                })}
                onBlur={async () => await trigger("country")}
                value={countries?.data?.find((item) => item.value === selectedCountry)}
                options={countries?.data}
                onChange={changeCountry}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>State</Form.Label>
              <Select
                // isSearchable={false}
                id="state"
                name="state"
                // className={` ${errors[item.code] ? "error-field" : ""}`}
                {...register("state", {
                  required: {
                    value: true,
                    message: t("this_field_is_required"),
                  },
                })}
                onBlur={async () => await trigger("state")}
                value={countryStates?.data?.find(
                  (item) => item.value === selectedState
                )}
                options={countryStates?.data}
                onChange={changeState}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>District</Form.Label>
               <Select
                // isSearchable={false}
                id="district"
                name="district"
                // className={` ${errors[item.code] ? "error-field" : ""}`}
                {...register("district", {
                  required: {
                    value: true,
                    message: t("this_field_is_required"),
                  },
                })}
                onBlur={async () => await trigger("district")}
                value={districts?.find(
                  (item) => item.value === selectedDistrict
                )}
                options={districts}
                onChange={changeDistrict}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>City</Form.Label>
              <Controller
                name="city"
                control={control}
                defaultValue=""
                rules={{
                  required: t("this_field_is_required"),
                  pattern: {
                    value: /^[a-zA-Z0-9\s]+$/,
                    message: t("invalid_format_space"),
                  },
                }}
                render={({ field }) => (
                  <>
                    <Form.Control {...field} type="text" />
                    {errors.city && (
                      <span className="text-danger">{errors.city.message}</span>
                    )}
                  </>
                )}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>{t("phone_number")}</Form.Label>
              <Controller
                name="phoneNumber"
                control={control}
                defaultValue=""
                rules={{
                  required: t("this_field_is_required"),
                }}
                render={({ field }) => (
                  <>
                    <PhoneInput
                      defaultCountry="in"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                    />
                    {errors.phoneNumber && (
                      <span className="text-danger">
                        {errors.phoneNumber.message}
                      </span>
                    )}
                  </>
                )}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" type="submit">
              {t("save_changes")}
            </Button>
            <Button variant="secondary" onClick={handleClose}>
              {t("close")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default AddressModal;
